(() => {
  'use strict';

  const root = typeof window === 'undefined' ? globalThis : window;
  const isFrench = typeof document !== 'undefined' && String(document.documentElement.lang || '').toLowerCase().startsWith('fr');
  const tr = (nl, fr) => (isFrench ? fr : nl);
  const LEGACY_STAFFEL_TITLE_PREFIX = 'Staffelkorting particulier';
  const STAFFEL_TITLE_PATTERN = /^\d+(?:[.,]\d+)?%\s+staffelkorting$/i;

  const isStaffelDiscountTitle = (title) => {
    const normalizedTitle = String(title || '').trim();
    return (
      normalizedTitle.startsWith(LEGACY_STAFFEL_TITLE_PREFIX) ||
      STAFFEL_TITLE_PATTERN.test(normalizedTitle)
    );
  };

  const normalizeTiers = (rawTiers) => {
    if (!Array.isArray(rawTiers) || rawTiers.length === 0) return [];

    const tiers = [];
    let previousMinimum = 0;
    let previousPercentage = 0;

    for (const rawTier of rawTiers) {
      const minimum = Number(rawTier?.minimum);
      const percentage = Number(rawTier?.percentage);

      if (
        !Number.isInteger(minimum) ||
        minimum < 2 ||
        !Number.isFinite(percentage) ||
        percentage <= 0 ||
        percentage >= 100 ||
        minimum <= previousMinimum ||
        percentage <= previousPercentage
      ) {
        return [];
      }

      tiers.push({ ...rawTier, minimum, percentage });
      previousMinimum = minimum;
      previousPercentage = percentage;
    }

    return tiers;
  };

  const tierForQuantity = (quantity, tiers) =>
    tiers.reduce((activeTier, tier) => (quantity >= tier.minimum ? tier : activeTier), null);

  const nextTierForQuantity = (quantity, tiers) =>
    tiers.find((tier) => quantity < tier.minimum) || null;

  const calculateDiscountedTotal = (basePrice, quantity, percentage = 0) => {
    const unitPrice = Math.max(0, Number(basePrice) || 0);
    const selectedQuantity = Math.max(1, Number.parseInt(quantity, 10) || 1);
    const discount = Math.max(0, Math.min(100, Number(percentage) || 0));

    return Math.round((unitPrice * selectedQuantity * (100 - discount)) / 100);
  };

  const summarizeCart = (cart, eligibleProductIds) => {
    const eligibleIds =
      eligibleProductIds instanceof Set
        ? eligibleProductIds
        : new Set(Array.from(eligibleProductIds || [], (id) => String(id)));
    const titles = new Set();
    const percentages = new Set();
    let eligibleQuantity = 0;
    let allocationCount = 0;
    let savings = 0;

    for (const item of cart?.items || []) {
      if (eligibleIds.has(String(item?.product_id))) {
        eligibleQuantity += Math.max(0, Number(item?.quantity) || 0);
      }

      for (const allocation of item?.line_level_discount_allocations || []) {
        const application = allocation?.discount_application || {};
        const title = String(application.title || '');

        if (!isStaffelDiscountTitle(title)) continue;

        allocationCount += 1;
        savings += Math.max(0, Number(allocation.amount) || 0);
        titles.add(title);

        if (application.value_type === 'percentage') {
          const percentage = Number(application.value);
          if (Number.isFinite(percentage)) percentages.add(percentage);
        }
      }
    }

    return {
      eligibleQuantity,
      allocationCount,
      savings,
      titles: Array.from(titles),
      percentages: Array.from(percentages),
      actualPercentage: percentages.size === 1 ? Array.from(percentages)[0] : null,
      allocationConflict: titles.size > 1 || percentages.size > 1
    };
  };

  const calculateState = ({
    cartLoaded,
    cartEligibleQuantity,
    selectedQuantity,
    tiers,
    allocationCount = 0,
    actualPercentage = null,
    allocationConflict = false
  }) => {
    const normalizedTiers = normalizeTiers(tiers);
    if (!normalizedTiers.length) return { state: 'INVALID', tiers: [] };

    const currentQuantity = Math.max(0, Number.parseInt(cartEligibleQuantity, 10) || 0);
    const selection = Math.max(1, Number.parseInt(selectedQuantity, 10) || 1);
    const projectedQuantity = currentQuantity + selection;
    const currentTier = tierForQuantity(currentQuantity, normalizedTiers);
    const projectedTier = tierForQuantity(projectedQuantity, normalizedTiers);
    const currentNextTier = nextTierForQuantity(currentQuantity, normalizedTiers);
    const projectedNextTier = nextTierForQuantity(projectedQuantity, normalizedTiers);
    const hasAllocation = allocationCount > 0;
    const allocationMatchesConfig =
      hasAllocation &&
      !allocationConflict &&
      currentTier !== null &&
      Number.isFinite(actualPercentage) &&
      Math.abs(actualPercentage - currentTier.percentage) < 0.000001;

    let state = 'LOADING';
    if (cartLoaded) {
      if (allocationMatchesConfig) {
        state = 'APPLIED';
      } else if (hasAllocation || currentTier) {
        state = 'MISMATCH';
      } else if (currentQuantity > 0) {
        state = 'PROGRESS';
      } else {
        state = 'EMPTY';
      }
    }

    return {
      state,
      tiers: normalizedTiers,
      currentQuantity,
      selectedQuantity: selection,
      projectedQuantity,
      currentTier,
      projectedTier,
      currentNextTier,
      projectedNextTier,
      hasAllocation,
      allocationMatchesConfig,
      configurationMismatch: cartLoaded && (hasAllocation || currentTier) && !allocationMatchesConfig,
      actualPercentage: Number.isFinite(actualPercentage) ? actualPercentage : null
    };
  };

  const logic = Object.freeze({
    STAFFEL_TITLE_PREFIX: LEGACY_STAFFEL_TITLE_PREFIX,
    isStaffelDiscountTitle,
    normalizeTiers,
    tierForQuantity,
    nextTierForQuantity,
    calculateDiscountedTotal,
    summarizeCart,
    calculateState
  });

  root.GEVolumePricingLogic = logic;

  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') return;
  if (customElements.get('ge-volume-pricing')) return;


  customElements.define(
    'ge-volume-pricing',
    class extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.cartRequestController = null;
        this.cartRequestNumber = 0;
        this.eligibilityRequestController = null;
        this.eligibilityRequestNumber = 0;
        this.cart = null;
        this.cartLoaded = false;
        this.cartSummary = summarizeCart(null, []);
        this.productTargeted = this.dataset.currentProductTargeted === 'true';
        this.variantPrices = this.readJson('[data-variant-prices]', {});
        this.variantAvailability = this.readJson('[data-variant-availability]', {});
        this.eligibleProductIds = new Set(
          Object.keys(this.readJson('[data-eligible-product-ids]', {}))
        );

        this.tiers = normalizeTiers(
          Array.from(this.querySelectorAll('[data-min-quantity]')).map((element) => ({
            element,
            minimum: Number(element.dataset.minQuantity),
            percentage: Number(element.dataset.percentage)
          }))
        );

        if (!this.tiers.length) {
          this.hidden = true;
          return;
        }

        const signal = this.abortController.signal;

        document.addEventListener('input', (event) => {
          if (event.target.matches('[name="quantity"]')) this.update();
        }, { signal });

        document.addEventListener('change', () => requestAnimationFrame(() => this.update()), {
          signal
        });

        document.addEventListener('click', (event) => {
          if (event.target.closest('.quantity-selector__button')) {
            requestAnimationFrame(() => this.update());
          }
        }, { signal });

        document.addEventListener('cart:change', (event) => {
          if (event.detail?.cart) {
            this.cancelCartRequest();
            this.setCart(event.detail.cart);
            this.loadEligibility();
          } else {
            this.loadCart();
          }
        }, { signal });

        document.addEventListener('cart:refresh', () => this.loadCart(), { signal });
        window.addEventListener('pageshow', () => this.loadCart(), { signal });
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') this.loadCart();
        }, { signal });

        this.update();
        this.loadEligibility();
        this.loadCart();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.cancelCartRequest();
        this.cancelEligibilityRequest();
      }

      readJson(selector, fallback) {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent || '');
        } catch {
          return fallback;
        }
      }

      async loadEligibility() {
        const section = this.closest('.shopify-section');
        const sectionId = section?.id?.replace(/^shopify-section-/, '');
        if (!sectionId) return;

        const requestNumber = this.eligibilityRequestNumber + 1;
        this.eligibilityRequestNumber = requestNumber;
        this.eligibilityRequestController?.abort();
        this.eligibilityRequestController = new AbortController();

        try {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', sectionId);
          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            headers: { accept: 'text/html' },
            signal: this.eligibilityRequestController.signal
          });

          if (!response.ok) {
            throw new Error(`Productsectie kon niet worden gelezen (${response.status})`);
          }

          const html = await response.text();
          if (requestNumber !== this.eligibilityRequestNumber || !this.isConnected) return;

          const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
          const parsedComponent = Array.from(
            parsedDocument.querySelectorAll('ge-volume-pricing')
          ).find((component) => component.dataset.currentProductId === this.dataset.currentProductId);
          const idsNode = parsedComponent?.querySelector('[data-eligible-product-ids]');
          if (!idsNode) throw new Error('Staffel-ID-lijst ontbreekt in de herladen productsectie');

          const ids = new Set(Object.keys(JSON.parse(idsNode.textContent || '{}')));

          this.eligibleProductIds = ids;
          this.productTargeted = ids.has(String(this.dataset.currentProductId));
          this.dataset.eligibilityVerified = 'true';

          this.cartSummary = summarizeCart(this.cart, this.eligibleProductIds);
          this.update();
        } catch (error) {
          if (error?.name === 'AbortError' || requestNumber !== this.eligibilityRequestNumber) return;
          this.dataset.eligibilityVerified = 'false';
          this.update();
        }
      }

      cancelEligibilityRequest() {
        this.eligibilityRequestNumber += 1;
        this.eligibilityRequestController?.abort();
        this.eligibilityRequestController = null;
      }

      cancelCartRequest() {
        this.cartRequestNumber += 1;
        this.cartRequestController?.abort();
        this.cartRequestController = null;
      }

      async loadCart() {
        const requestNumber = this.cartRequestNumber + 1;
        this.cartRequestNumber = requestNumber;
        this.cartRequestController?.abort();
        this.cartRequestController = new AbortController();

        try {
          const response = await fetch(this.dataset.cartUrl, {
            credentials: 'same-origin',
            headers: { accept: 'application/json' },
            signal: this.cartRequestController.signal
          });

          if (!response.ok) throw new Error(`Winkelwagen kon niet worden gelezen (${response.status})`);
          const cart = await response.json();
          if (requestNumber !== this.cartRequestNumber || !this.isConnected) return;
          this.setCart(cart);
          this.loadEligibility();
        } catch (error) {
          if (error?.name === 'AbortError' || requestNumber !== this.cartRequestNumber) return;
          this.cart = null;
          this.cartLoaded = false;
          this.cartSummary = summarizeCart(null, this.eligibleProductIds);
          this.update();
        }
      }

      setCart(cart) {
        this.cart = cart;
        this.cartLoaded = true;
        this.cartSummary = summarizeCart(cart, this.eligibleProductIds);
        this.update();
      }

      getProductSelection() {
        const section = this.closest('.shopify-section') || document;
        const form = section.querySelector('form[action*="/cart/add"]:not(.ge-required-sensor__form)');
        const quantityInput =
          form?.querySelector('[name="quantity"]') || section.querySelector('[name="quantity"]');
        const variantInput = form?.querySelector('[name="id"]');
        const selectedQuantity = Math.max(1, Number.parseInt(quantityInput?.value, 10) || 1);
        const variantId = String(variantInput?.value || this.dataset.initialVariant || '');
        const basePrice = Number(
          this.variantPrices[variantId] ?? this.variantPrices[this.dataset.initialVariant]
        );
        const available = this.variantAvailability[variantId] !== false;

        return { selectedQuantity, variantId, basePrice, available };
      }

      update() {
        if (!this.tiers.length) return;

        const selection = this.getProductSelection();
        if (!Number.isFinite(selection.basePrice)) return;

        const state = calculateState({
          cartLoaded: this.cartLoaded,
          cartEligibleQuantity: this.cartSummary.eligibleQuantity,
          selectedQuantity: selection.selectedQuantity,
          tiers: this.tiers,
          allocationCount: this.cartSummary.allocationCount,
          actualPercentage: this.cartSummary.actualPercentage,
          allocationConflict: this.cartSummary.allocationConflict
        });
        const money = new Intl.NumberFormat(this.dataset.locale, {
          style: 'currency',
          currency: this.dataset.currency
        });

        this.dataset.cartState = state.state.toLowerCase();
        this.dataset.currentEligibleQuantity = String(state.currentQuantity);
        this.dataset.projectedEligibleQuantity = String(state.projectedQuantity);
        this.dataset.staffelAllocationCount = String(this.cartSummary.allocationCount);
        this.dataset.staffelSavings = String(this.cartSummary.savings);
        this.dataset.actualDiscountPercentage =
          state.actualPercentage === null ? '' : String(state.actualPercentage);

        this.updateTierRows(state, selection.basePrice, money);
        this.updateCurrentCartState(state, money);
        this.updateProjection(state, selection, money);
      }

      updateTierRows(state, basePrice, money) {
        this.tiers.forEach((tier, index) => {
          const matchesTier = (candidate) =>
            candidate?.minimum === tier.minimum && candidate?.percentage === tier.percentage;
          const isApplied =
            state.state === 'APPLIED' && state.actualPercentage === tier.percentage;
          const isProjected = matchesTier(state.projectedTier);
          const isNext = matchesTier(state.projectedNextTier);
          const isBest = index === this.tiers.length - 1;
          const remaining = Math.max(0, tier.minimum - state.projectedQuantity);
          const tierPrice = Math.round((basePrice * (100 - tier.percentage)) / 100);
          const label = tier.element.querySelector('[data-tier-state]');

          tier.element.classList.toggle('is-applied', isApplied);
          tier.element.classList.toggle('is-projected', isProjected);
          tier.element.classList.toggle('is-best', isBest);
          tier.element.querySelector('[data-tier-price]').textContent = money.format(tierPrice / 100);

          if (isApplied) {
            label.textContent = tr('Nu toegepast', 'Appliqué');
          } else if (isProjected) {
            label.textContent = tr('Na toevoegen', 'Après ajout');
          } else if (isNext) {
            label.textContent = isFrench ? `Encore ${remaining} ${remaining === 1 ? 'unité' : 'unités'}` : `Nog ${remaining} ${remaining === 1 ? 'stuk' : 'stuks'}`;
          } else if (isBest) {
            label.textContent = tr('Meeste voordeel', 'Meilleure remise');
          } else {
            label.textContent = '';
          }
        });
      }

      updateCurrentCartState(state, money) {
        const container = this.querySelector('[data-cart-state]');
        const title = this.querySelector('[data-cart-state-title]');
        const detail = this.querySelector('[data-cart-state-detail]');
        const quantityLabel = isFrench
          ? `${state.currentQuantity} ${state.currentQuantity === 1 ? 'article participant' : 'articles participants'}`
          : `${state.currentQuantity} deelnemende ${
              state.currentQuantity === 1 ? 'artikel' : 'artikelen'
            }`;

        container.classList.toggle('is-applied', state.state === 'APPLIED');
        container.classList.toggle('is-mismatch', state.state === 'MISMATCH');

        if (state.state === 'LOADING') {
          title.textContent = tr('Winkelwagen wordt gecontroleerd…', 'Vérification du panier…');
          detail.textContent = tr('Tot de controle klaar is, wordt geen actieve staffelkorting getoond.', 'Aucune remise n’est affichée tant que la vérification n’est pas terminée.');
        } else if (state.state === 'APPLIED') {
          title.textContent = isFrench ? `${state.actualPercentage}% de remise sur quantité appliquée par Shopify` : `${state.actualPercentage}% staffelkorting toegepast door Shopify`;
          detail.textContent = isFrench
            ? `${quantityLabel} · ${money.format(
                this.cartSummary.savings / 100
              )} de remise réelle dans votre panier.`
            : `${quantityLabel} · ${money.format(
                this.cartSummary.savings / 100
              )} echte korting in je huidige winkelwagen.`;
        } else if (state.state === 'MISMATCH' && state.hasAllocation) {
          title.textContent = tr('De Shopify-korting wijkt af van de ingestelde staffel', 'La remise Shopify diffère du barème configuré');
          detail.textContent = isFrench
            ? `${money.format(
                this.cartSummary.savings / 100
              )} a réellement été attribué. Le total du panier fait foi.`
            : `${money.format(
                this.cartSummary.savings / 100
              )} is werkelijk toegewezen. Het winkelwagentotaal is leidend.`;
        } else if (state.state === 'MISMATCH') {
          title.textContent = tr('Nog geen staffelkorting toegepast', 'Aucune remise sur quantité appliquée');
          detail.textContent = isFrench ? `${quantityLabel} ; le seuil est atteint, mais Shopify n’a appliqué aucune remise sur quantité. Le total du panier fait foi.` : `${quantityLabel}; de drempel is bereikt, maar Shopify heeft geen staffelkorting toegewezen. Het winkelwagentotaal is leidend.`;
        } else {
          title.textContent = tr('Nog geen staffelkorting toegepast', 'Aucune remise sur quantité appliquée');

          if (state.currentNextTier) {
            const remaining = state.currentNextTier.minimum - state.currentQuantity;
            detail.textContent = isFrench
              ? `${quantityLabel} · encore ${remaining} ${remaining === 1 ? 'article' : 'articles'} avant ${state.currentNextTier.percentage} %.`
              : `${quantityLabel} · nog ${remaining} ${
                  remaining === 1 ? 'artikel' : 'artikelen'
                } tot ${state.currentNextTier.percentage}%.`;
          } else {
            detail.textContent = quantityLabel;
          }
        }
      }

      updateProjection(state, selection, money) {
        const label = this.querySelector('[data-projected-discount]');
        const price = this.querySelector('[data-projected-unit-price]');
        const status = this.querySelector('[data-result-status]');
        const context = this.querySelector('[data-cart-context]');
        let projectedPercentage = state.projectedTier?.percentage || 0;
        const projectedArticleLabel = isFrench
          ? `${state.projectedQuantity} ${state.projectedQuantity === 1 ? 'article participant' : 'articles participants'}`
          : `${state.projectedQuantity} ${
              state.projectedQuantity === 1 ? 'deelnemend artikel' : 'deelnemende artikelen'
            }`;

        if (!this.productTargeted || !selection.available || state.state === 'LOADING') {
          projectedPercentage = 0;
        }

        this.updateCartButtonPrice(selection, projectedPercentage, money);

        const currentDiscount = this.querySelector('[data-current-selection-discount]');
        if (currentDiscount) {
          const quantity = selection.selectedQuantity;
          const ready = selection.available && this.productTargeted && state.state !== 'LOADING' && state.state !== 'MISMATCH';
          const originalTotal = selection.basePrice * quantity;
          const saving = originalTotal - Math.round(originalTotal * (100 - projectedPercentage) / 100);
          this.querySelector('[data-current-selection-label]').textContent = isFrench
            ? 'Remise pour ' + quantity + (quantity === 1 ? ' unité' : ' unités')
            : 'Korting bij ' + quantity + (quantity === 1 ? ' stuk' : ' stuks');
          currentDiscount.parentElement.dataset.discountActive = String(ready && projectedPercentage > 0);
          currentDiscount.textContent = !selection.available ? tr('Niet beschikbaar', 'Indisponible')
            : state.state === 'MISMATCH' ? tr('Controleer je winkelwagen', 'Vérifiez votre panier')
            : !ready ? tr('Wordt berekend…', 'Calcul en cours…')
            : projectedPercentage > 0 ? projectedPercentage + '% · ' + money.format(saving / 100) + tr(' voordeel', ' d’économie')
            : '0%';
        }

        const projectedUnitPrice = Math.round(
          (selection.basePrice * (100 - projectedPercentage)) / 100
        );
        price.textContent = money.format(projectedUnitPrice / 100);

        if (!selection.available) {
          label.textContent = tr('Geen staffelprojectie', 'Aucune estimation de remise');
          status.textContent = tr('Deze variant is niet beschikbaar en kan niet worden toegevoegd.', 'Cette variante est indisponible et ne peut pas être ajoutée.');
          context.textContent = tr('Er wordt geen korting voorspeld voor een niet-beschikbare variant.', 'Aucune remise n’est estimée pour une variante indisponible.');
          return;
        }

        if (state.state === 'LOADING') {
          label.textContent = tr('Projectie wordt gecontroleerd', 'Vérification de l’estimation');
          status.textContent = tr('De winkelwagen kon nog niet betrouwbaar worden gelezen.', 'Le panier n’a pas encore pu être lu de manière fiable.');
          context.textContent = tr('Er wordt pas een verwachting getoond na een geslaagde winkelwagencontrole.', 'Une estimation s’affiche après une vérification réussie du panier.');
          return;
        }

        if (!this.productTargeted) {
          label.textContent = tr('Nog niet bevestigd als staffelproduct', 'Produit non encore confirmé pour les remises sur quantité');
          status.textContent = tr('Shopify heeft dit product nog niet in de kortingscollectie bevestigd.', 'Shopify n’a pas encore confirmé ce produit dans la collection de remise.');
          context.textContent = tr('Wacht na het aanzetten van deelname tot de automatische collectie is bijgewerkt.', 'Après activation, attendez la mise à jour de la collection automatique.');
          return;
        }

        if (this.hasAttribute('data-clear-feedback')) {
          if (state.projectedTier) {
            label.textContent = isFrench ? `Prix avec ${state.projectedTier.percentage} % de remise` : `Prijs bij ${state.projectedTier.percentage}% staffelkorting`;
            status.textContent = isFrench ? `Avec votre sélection : ${projectedArticleLabel} dans votre panier.` : `Met jouw selectie: ${projectedArticleLabel} in je winkelwagen.`;
            context.textContent = isFrench ? 'Estimation après ajout. La remise définitive est affichée dans votre panier.' : 'Prijsindicatie na toevoegen. Je ziet de definitieve korting in je winkelwagen.';
          } else if (state.projectedNextTier) {
            const remaining = state.projectedNextTier.minimum - state.projectedQuantity;
            label.textContent = tr('Prijs zonder staffelkorting', 'Prix sans remise sur quantité');
            status.textContent = isFrench ? `Encore ${remaining} ${remaining === 1 ? 'article' : 'articles'} pour bénéficier de ${state.projectedNextTier.percentage} % de remise.` : `Nog ${remaining} ${remaining === 1 ? 'artikel' : 'artikelen'} extra voor ${state.projectedNextTier.percentage}% korting.`;
            context.textContent = isFrench ? `Avec votre sélection : ${projectedArticleLabel} dans votre panier. Les produits participants sont cumulés.` : `Met jouw selectie: ${projectedArticleLabel} in je winkelwagen. Deelnemende producten tellen samen.`;
          } else {
            label.textContent = tr('Prijs per stuk', 'Prix unitaire');
            status.textContent = isFrench ? `Avec votre sélection : ${projectedArticleLabel} dans votre panier.` : `Met jouw selectie: ${projectedArticleLabel} in je winkelwagen.`;
            context.textContent = tr('Je ziet de definitieve korting in je winkelwagen.', 'La remise définitive est affichée dans votre panier.');
          }
          return;
        }

        if (state.projectedTier) {
          label.textContent = isFrench ? `Après ajout : ${state.projectedTier.percentage} % prévu` : `Na toevoegen: ${state.projectedTier.percentage}% verwacht`;

          if (
            state.state === 'APPLIED' &&
            state.actualPercentage === state.projectedTier.percentage
          ) {
            status.textContent = isFrench ? `Après ajout : ${projectedArticleLabel} ; la remise de ${state.projectedTier.percentage} % devrait rester applicable.` : `Na toevoegen: ${projectedArticleLabel}; ${state.projectedTier.percentage}% blijft naar verwachting gelden.`;
          } else {
            status.textContent = isFrench ? `Après ajout : ${projectedArticleLabel} ; une remise de ${state.projectedTier.percentage} % est prévue.` : `Na toevoegen: ${projectedArticleLabel}; dan wordt ${state.projectedTier.percentage}% verwacht.`;
          }

          context.textContent = tr('Dit is een verwachting. Shopify bevestigt en verrekent de echte korting pas in de winkelwagen en checkout.', 'Il s’agit d’une estimation. Shopify confirme et applique la remise réelle dans le panier et lors du paiement.');
        } else if (state.projectedNextTier) {
          const remaining = state.projectedNextTier.minimum - state.projectedQuantity;
          label.textContent = tr('Na toevoegen: nog geen korting', 'Après ajout : aucune remise');
          status.textContent = isFrench
            ? `Après ajout : ${projectedArticleLabel} ; encore ${remaining} ${remaining === 1 ? 'article' : 'articles'} avant ${state.projectedNextTier.percentage} %.`
            : `Na toevoegen: ${projectedArticleLabel}; nog ${remaining} ${
                remaining === 1 ? 'artikel' : 'artikelen'
              } tot ${state.projectedNextTier.percentage}%.`;
          context.textContent = tr('Alleen producten in de staffelcollectie tellen mee.', 'Seuls les produits de la collection de remise sont pris en compte.');
        } else {
          label.textContent = tr('Na toevoegen', 'Après ajout');
          status.textContent = isFrench ? `${projectedArticleLabel} après ajout.` : `${projectedArticleLabel} na toevoegen.`;
          context.textContent = tr('Shopify blijft leidend voor de uiteindelijke korting.', 'Shopify détermine la remise définitive.');
        }
      }

      updateCartButtonPrice(selection, percentage, money) {
        const section = this.closest('.shopify-section') || document;
        const total = calculateDiscountedTotal(
          selection.basePrice,
          selection.selectedQuantity,
          percentage
        );

        section.querySelectorAll('[data-ge-cart-total]').forEach((price) => {
          price.textContent = money.format(total / 100);
        });
      }
    }
  );
})();
